import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import ReactDOM from 'react-dom';
import {
  PageContent,
  Spinner,
  CisContainer,
  Button,
  Row,
  ButtonIcon,
  Modal,
  Table,
  Col,
} from 'components';
import PropTypes from 'prop-types';
import { globalPropTypes } from 'helpers';
import { exportForecastReportToExcel } from 'api/services/cis';
import StatisticsSelect from 'containers/ipma/Cis-Cvmb/Statistics/StatisticsSelect';
import TableHeaderCell from 'components/Table/TableHeaderCell';
import CisTable from 'components/Table/CisTable';
import CisPagination from 'components/Table/CisPagination';
import checkPermissions from '../../../helpers/checkPermissions';

const Forecasts = ({
  loading,
  type,
  list,
  loadList,
  setPage,
  setPerPage,
  setSort,
  setFilters,
  removeFilters,
  setProfile,
  removeForecast,
  levelDomains,
  levelDomainsLevels,
  levelDomainsDomains,
  loadForecastsAttributes,
  userPermissions,
}) => {
  const { filters, items, page, pages, perPage, sort, totalItems } = list;
  const { t } = useTranslation();
  const history = useHistory();
  const [portalReady, setPortalReady] = useState(false);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [selectedYear, setSelectedYear] = useState('');

  const isIpmaCvmb = type === 'ipma-cvmb';
  const manageCisPermission = checkPermissions({ roles: ['ma_manage_cis'] }, userPermissions || []);

  const currentYear = new Date().getFullYear();
  const yearOptions = [];
  for (let year = currentYear - 10; year <= currentYear + 1; year++) {
    yearOptions.push({ label: year.toString(), value: year.toString() });
  }

  const handleExport = async () => {
    if (!selectedYear) {
      return;
    }

    try {
      setIsExporting(true);
      await exportForecastReportToExcel({ year: selectedYear });
    } catch (error) {
      console.error('Error exporting forecasts:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleYearChange = (option) => {
    setSelectedYear(option ? option.value : '');
  };

  useEffect(() => {
    if (isIpmaCvmb) {
      if (document.getElementById('ipma-cis-cvmb-statistics-actions-portal')) {
        setPortalReady(true);
      }
    } else {
      if (document.getElementById(`${type}-cis-buttons-portal`)) {
        setPortalReady(true);
      }
    }
  }, [isIpmaCvmb, type]);

  useEffect(() => {
    setPage(1);
    loadList();
  }, [perPage, sort]);

  useEffect(() => {
    loadForecastsAttributes();
  }, []);

  const getSortDirection = (key) => {
    if (!sort) return null;
    const [sortKey, sortDir] = sort.split('.');
    return sortKey === key ? sortDir : null;
  };

  const handleColumnSort = (key, direction) => {
    if (direction) {
      setSort(`${key}.${direction}`);
    } else {
      setSort(null);
    }
  };

  const handleColumnFilterChange = (key, value) => {
    setFilters({ name: key, value });
    setPage(1);
    loadList();
  };

  return (
    <PageContent>
      <CisContainer>
        <CisTable>
          <CisTable.Head>
            <CisTable.Row>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="select"
                  filterValue={filters.levelDomainLevel || ''}
                  onFilter={(val) => handleColumnFilterChange('levelDomainLevel', val)}
                  filterOptions={levelDomainsLevels}
                  filterPlaceholder={t('page.user.cis.forecasts.levelDomainLevelPlaceholder')}
                  label={t('page.user.cis.forecasts.level')}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="select"
                  filterValue={filters.levelDomainDomain || ''}
                  onFilter={(val) => handleColumnFilterChange('levelDomainDomain', val)}
                  filterOptions={levelDomainsDomains}
                  filterPlaceholder={t('page.user.cis.forecasts.levelDomainDomainPlaceholder')}
                  label={t('page.user.cis.forecasts.domain')}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="input"
                  filterValue={filters.year || ''}
                  onFilter={(val) => handleColumnFilterChange('year', val)}
                  filterPlaceholder={t('page.user.cis.forecasts.yearPlaceholder')}
                  label={t('page.user.cis.forecasts.year')}
                  sort
                  sortDirection={getSortDirection('year')}
                  onSort={(dir) => handleColumnSort('year', dir)}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell label={t('page.user.cis.forecasts.newAdded')} />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell label={t('page.user.cis.forecasts.renewAdded')} />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="input"
                  filterValue={filters.user || ''}
                  onFilter={(val) => handleColumnFilterChange('user', val)}
                  filterPlaceholder={t('page.user.cis.forecasts.userPlaceholder')}
                  label={t('page.user.cis.forecasts.user')}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell label={t('page.user.cis.forecasts.date')} />
              </CisTable.StickyHeading>
              {!isIpmaCvmb && manageCisPermission && (
                <CisTable.StickyActionHeading style={{ minWidth: '10rem' }}>
                  <TableHeaderCell
                    label={t('page.user.cis.forecasts.actions')}
                    labelAlign="center"
                    isAction={true}
                  />
                </CisTable.StickyActionHeading>
              )}
            </CisTable.Row>
          </CisTable.Head>
          <CisTable.Body>
            {loading ? (
              <CisTable.Row>
                <CisTable.Cell colSpan={isIpmaCvmb ? 7 : 8}>
                  <Spinner />
                </CisTable.Cell>
              </CisTable.Row>
            ) : null}
            {!loading && !items.length ? (
              <CisTable.Row>
                <CisTable.Cell colSpan={isIpmaCvmb ? 7 : 8}>
                  {t('global.noResultsFound')}
                </CisTable.Cell>
              </CisTable.Row>
            ) : null}
            {!loading && items.length
              ? items.map((item) => (
                  <CisTable.Row key={item.id}>
                    <CisTable.Cell>{item.levelDomain?.level?.name || ''}</CisTable.Cell>
                    <CisTable.Cell>{item.levelDomain?.domain?.name || ''}</CisTable.Cell>
                    <CisTable.Cell>{item.year}</CisTable.Cell>
                    <CisTable.Cell>{item.newAdded}</CisTable.Cell>
                    <CisTable.Cell>{item.renewAdded}</CisTable.Cell>
                    <CisTable.Cell>{item.user?.email || ''}</CisTable.Cell>
                    <CisTable.Cell>
                      {item.updatedAt
                        ? moment(item.updatedAt).format('YYYY-MM-DD HH:mm')
                        : item.createdAt
                        ? moment(item.createdAt).format('YYYY-MM-DD HH:mm')
                        : ''}
                    </CisTable.Cell>
                    {!isIpmaCvmb && manageCisPermission && (
                      <CisTable.StickyActionCell style={{ minWidth: '9.6rem' }}>
                        <Row style={{ gap: '3px', justifyContent: 'center' }}>
                          <ButtonIcon
                            icon="edit"
                            secondary
                            as={Button}
                            onClick={() => {
                              setProfile(item);
                              history.push(`/${type}/cis/forecasts/edit`);
                            }}
                          />
                          <ButtonIcon
                            icon="trash"
                            onClick={() => {
                              setSelectedItem(item);
                              setOpenConfirmModal(true);
                            }}
                          />
                        </Row>
                      </CisTable.StickyActionCell>
                    )}
                  </CisTable.Row>
                ))
              : null}
          </CisTable.Body>
        </CisTable>
      </CisContainer>
      <CisPagination
        styled
        currentPage={page}
        totalPages={pages}
        totalItems={totalItems}
        perPage={perPage}
        onPageChange={(payload) => {
          setPage(payload);
          loadList();
        }}
        onPerPageChange={({ value }) => setPerPage(value)}
      />
      <Modal
        heading={t('page.user.cis.forecasts.deleteConfirmMessage')}
        isOpen={openConfirmModal && selectedItem?.id}
        confirm={() => {
          removeForecast(selectedItem.id);
          setOpenConfirmModal(false);
        }}
        cancel={() => setOpenConfirmModal(false)}
        onRequestClose={() => setOpenConfirmModal(false)}
      />
      {portalReady &&
        !isIpmaCvmb &&
        ReactDOM.createPortal(
          <Row style={{ alignItems: 'center' }}>
            {manageCisPermission && (
              <Col size={{ xs: 1 / 3 }}>
                <Button onClick={() => history.push(`/${type}/cis/forecasts/create`)} secondary>
                  {t('page.user.cis.forecasts.addForecast')}
                </Button>
              </Col>
            )}
            <Col size={{ xs: 1 / 3 }}>
              <StatisticsSelect
                options={yearOptions}
                value={yearOptions.find((option) => option.value === selectedYear) || null}
                onChange={handleYearChange}
                placeholder={t('page.user.cis.forecasts.selectYear')}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                isClearable={true}
              />
            </Col>
            <Col size={{ xs: 1 / 3 }}>
              <Button secondary onClick={handleExport} disabled={isExporting || !selectedYear}>
                {isExporting ? t('global.exporting') : t('global.export')}
              </Button>
            </Col>
          </Row>,
          document.getElementById(`${type}-cis-buttons-portal`),
        )}
      {portalReady &&
        isIpmaCvmb &&
        ReactDOM.createPortal(
          <Row>
            <Col size={{ xs: 1 / 2 }}>
              <StatisticsSelect
                options={yearOptions}
                value={yearOptions.find((option) => option.value === selectedYear) || null}
                onChange={handleYearChange}
                placeholder={t('page.user.cis.forecasts.selectYear')}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                isClearable={true}
              />
            </Col>
            <Col size={{ xs: 1 / 2 }}>
              <Button secondary onClick={handleExport} disabled={isExporting || !selectedYear}>
                {isExporting ? t('global.exporting') : t('global.export')}
              </Button>
            </Col>
          </Row>,
          document.getElementById('ipma-cis-cvmb-statistics-actions-portal'),
        )}
    </PageContent>
  );
};

Forecasts.propTypes = {
  loading: PropTypes.bool,
  type: PropTypes.oneOf(['ma', 'ipma-cvmb']),
  list: globalPropTypes.ListPropsTypes.isRequired,
  loadList: PropTypes.func.isRequired,
  setPage: PropTypes.func.isRequired,
  setPerPage: PropTypes.func.isRequired,
  setSort: PropTypes.func.isRequired,
  setFilters: PropTypes.func.isRequired,
  removeFilters: PropTypes.func.isRequired,
  setProfile: PropTypes.func.isRequired,
  removeForecast: PropTypes.func.isRequired,
  levelDomains: PropTypes.arrayOf(globalPropTypes.ChooseDataPropsTypes).isRequired,
  levelDomainsLevels: PropTypes.arrayOf(globalPropTypes.ChooseDataPropsTypes).isRequired,
  levelDomainsDomains: PropTypes.arrayOf(globalPropTypes.ChooseDataPropsTypes).isRequired,
  loadForecastsAttributes: PropTypes.func.isRequired,
};

Forecasts.defaultProps = {
  loading: false,
  type: 'ma',
};

export default Forecasts;
